import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import GlobalDiagnosticPopup from './GlobalDiagnosticPopup';

// Octave-folding frequency → color (mirrors iOS TorusColors.swift)
// Same musical note = same color regardless of octave
const frequencyToColor = (frequency) => {
  if (!frequency || frequency <= 0) return new THREE.Color(0.15, 0.15, 0.15); // dark grey for silence
  const hue = ((Math.log2(frequency / 80)) % 1.0 + 1.0) % 1.0;
  // Match iOS: HSB(h, 1.0, 1.0) = HSL(h, 1.0, 0.5)
  return new THREE.Color().setHSL(hue, 1.0, 0.5);
};

// Fonction pour calculer la position sur une spirale harmonique
const calculateSpiralPosition = (time, baseRadius, expansionFactor) => {
  const theta = time * 0.5; // Angle scalaire
  const k = Math.log(1.618); // Facteur d'expansion harmonique (nombre d'or)
  const r = baseRadius * Math.exp(k * theta);
  return {
    x: r * Math.cos(theta),
    y: r * Math.sin(theta),
    z: r * Math.sin(theta * 0.5)
  };
};

// Fonction pour calculer la respiration scalaire
const calculateScalarBreathing = (time, baseValue, amplitude) => {
  const breathingRate = 0.3; // Rythme de respiration naturel
  const phaseShift = Math.sin(time * 0.2) * 0.5; // Décalage de phase harmonique
  return baseValue + amplitude * Math.sin(time * breathingRate + phaseShift);
};

// 🌈 ARC-EN-CIEL QUI PÈTE - Couleurs vibrantes pour progression visuelle !
const DIAGNOSTIC_COLORS = [];

// Générer un arc-en-ciel ultra-vibrant pour 42 lignes
for (let i = 0; i < 42; i++) {
  const color = new THREE.Color();
  
  // Arc-en-ciel complet sur 360° avec couleurs saturées au maximum
  const hue = (i / 42) * 360; // Distribution uniforme sur tout le spectre
  const saturation = 1.0; // Saturation maximale pour que ça PÈTE ! 🔥
  const lightness = 0.6; // Luminosité optimale pour visibilité
  
  color.setHSL(hue / 360, saturation, lightness);
  DIAGNOSTIC_COLORS.push(color.getHex());
}


// Répartition basée sur le nombre d'or et les harmoniques
const PHI = 1.618033988749895; // Nombre d'or
const THETA_0 = 42 * Math.PI; // 131.946891... (invariant nodal)

// 42 lignes verticales = 42 harmoniques futures (mapping direct 1:1)
console.log('🌈 Arc-en-ciel progressif : 42 couleurs vibrantes qui pètent !');
console.log('🔥 Couleurs ultra-saturées:', DIAGNOSTIC_COLORS.map((c, i) => `${i}: #${c.toString(16).padStart(6, '0')}`).slice(0, 10));

// 🌀 THOR 42 QUANTUM SHADER LINES - Rendu harmonique selon θ₀ = 42π
const quantumLineShader = {
  vertexShader: `
    uniform float time;
    uniform float theta0;
    uniform float phi;
    uniform float selectedIndex;
    attribute float lineIndex;
    attribute vec3 color;
    varying vec3 vColor;
    varying float vLineIndex;
    varying float vSelected;
    
    void main() {
      vColor = color;
      vLineIndex = lineIndex;
      vSelected = (lineIndex == selectedIndex) ? 1.0 : 0.0;
      
      // Animation harmonique THOR 42
      vec3 pos = position;
      float harmonicPhase = theta0 * lineIndex / 42.0;
      float goldenRatio = phi;
      
      // Pulsation quantique selon le nombre d'or
      float pulse = sin(time * goldenRatio + harmonicPhase) * 0.1;
      pos *= (1.0 + pulse * vSelected);
      
      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      gl_PointSize = (vSelected > 0.5) ? 8.0 : 4.0;
    }
  `,
  
  fragmentShader: `
    uniform float time;
    uniform float theta0;
    uniform float phi;
    varying vec3 vColor;
    varying float vLineIndex;
    varying float vSelected;
    
    void main() {
      // Épaisseur harmonique θ₀ = 42π
      float harmonic = sin(theta0 * vLineIndex / 42.0) * 0.3 + 0.7;
      
      // Luminosité selon sélection et nombre d'or
      float brightness = vSelected > 0.5 ? (1.0 + sin(time * phi) * 0.5) : harmonic;
      
      // Couleur quantique THOR 42
      vec3 finalColor = vColor * brightness;
      
      // Alpha harmonique
      float alpha = 0.6 + harmonic * 0.4;
      
      gl_FragColor = vec4(finalColor, alpha);
    }
  `
};

// Fonction pour créer des lignes quantum THOR 42
function createQuantumTorusLines(R = 1, tube = 0.45, radialSegments = 42, tubularSegments = 42, phaseOffset = 0, diagnosticData = null, selectedDiagnosticIndex = -1) {
  const geometry = new THREE.BufferGeometry();
  const positions = [];
  const colors = [];
  const lineIndices = [];
  const indices = [];
  const r = R;
  
  // Générer les points avec métadonnées quantiques
  for (let i = 0; i <= radialSegments; i++) {
    const φ = (i / radialSegments) * 2 * Math.PI - phaseOffset;
    for (let j = 0; j <= tubularSegments; j++) {
      const ω = (j / tubularSegments) * 2 * Math.PI + phaseOffset;
      
      // Harmoniques THOR 42 : Horn Torus
      const x = r * (1 - Math.cos(φ)) * Math.sin(ω);
      const y = r * (1 - Math.cos(φ)) * Math.cos(ω);
      const z = r * Math.sin(φ);
      
      positions.push(x, y, z);
      
      // Couleurs par fréquence (octave folding) ou fallback arc-en-ciel
      let color = new THREE.Color(1, 1, 1);
      if (diagnosticData && diagnosticData[j]) {
        const metric = diagnosticData[j];
        if (metric.frequency > 0) {
          color = frequencyToColor(metric.frequency, (metric.resonance || 0) / 100);
        } else {
          color = new THREE.Color(DIAGNOSTIC_COLORS[j]);
        }
        if (j === selectedDiagnosticIndex) {
          color.multiplyScalar(1.5);
        }
      }
      
      colors.push(color.r, color.g, color.b);
      lineIndices.push(j); // Index de ligne pour shader
    }
  }
  
  // Créer les lignes selon topologie THOR 42
  for (let i = 0; i <= radialSegments; i++) {
    for (let j = 0; j < tubularSegments; j++) {
      const a = i * (tubularSegments + 1) + j;
      const b = i * (tubularSegments + 1) + (j + 1);
      indices.push(a, b);
    }
  }
  
  for (let i = 0; i < radialSegments; i++) {
    for (let j = 0; j <= tubularSegments; j++) {
      const a = i * (tubularSegments + 1) + j;
      const b = (i + 1) * (tubularSegments + 1) + j;
      indices.push(a, b);
    }
  }
  
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setAttribute('lineIndex', new THREE.Float32BufferAttribute(lineIndices, 1));
  geometry.setIndex(indices);
  
  return geometry;
}

// Fonction pour créer des tubes épais au lieu de lignes
function createThickTorusLines(R = 1, tube = 0.45, radialSegments = 42, tubularSegments = 42, phaseOffset = 0, diagnosticData = null, selectedDiagnosticIndex = -1, lineThickness = 0.02) {
  const group = new THREE.Group();
  const r = R;
  
  // Créer des tubes pour les lignes verticales
  for (let j = 0; j <= tubularSegments; j++) {
    const ω = (j / tubularSegments) * 2 * Math.PI + phaseOffset;
    const points = [];
    
    // Générer les points le long d'une ligne verticale
    for (let i = 0; i <= radialSegments; i++) {
      const φ = (i / radialSegments) * 2 * Math.PI - phaseOffset;
      
      const x = r * (1 - Math.cos(φ)) * Math.sin(ω);
      const y = r * (1 - Math.cos(φ)) * Math.cos(ω);
      const z = r * Math.sin(φ);
      
      points.push(new THREE.Vector3(x, y, z));
    }
    
    // Créer une géométrie de tube pour cette ligne
    const curve = new THREE.CatmullRomCurve3(points);
    
    // Déterminer l'épaisseur et la couleur
    let currentThickness = lineThickness;
    let color = new THREE.Color(1, 1, 1);
    
    if (diagnosticData && diagnosticData[j]) {
      const metric = diagnosticData[j];
      if (metric.frequency > 0) {
        color = frequencyToColor(metric.frequency, (metric.resonance || 0) / 100);
      } else {
        color = new THREE.Color(DIAGNOSTIC_COLORS[j]);
      }
      if (j === selectedDiagnosticIndex) {
        currentThickness = lineThickness * 2;
        color.multiplyScalar(1.5);
      }
    }
    
    const tubeGeometry = new THREE.TubeGeometry(curve, 64, currentThickness, 8, false);
    const tubeMaterial = new THREE.MeshBasicMaterial({ 
      color: color,
      transparent: true,
      opacity: (diagnosticData && diagnosticData[j]) ? 0.8 : 0.1
    });
    
    const tubeMesh = new THREE.Mesh(tubeGeometry, tubeMaterial);
    tubeMesh.userData = { lineIndex: j }; // Pour la détection de clic
    group.add(tubeMesh);
  }
  
  // Créer des tubes pour les lignes horizontales
  for (let i = 0; i <= radialSegments; i++) {
    const φ = (i / radialSegments) * 2 * Math.PI - phaseOffset;
    const points = [];
    
    for (let j = 0; j <= tubularSegments; j++) {
      const ω = (j / tubularSegments) * 2 * Math.PI + phaseOffset;
      
      const x = r * (1 - Math.cos(φ)) * Math.sin(ω);
      const y = r * (1 - Math.cos(φ)) * Math.cos(ω);
      const z = r * Math.sin(φ);
      
      points.push(new THREE.Vector3(x, y, z));
    }
    
    const curve = new THREE.CatmullRomCurve3(points);
    const tubeGeometry = new THREE.TubeGeometry(curve, 64, lineThickness * 0.7, 8, true);
    const tubeMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xffffff,
      transparent: true,
      opacity: diagnosticData ? 0.6 : 0.5 // Opacité plus élevée et cohérente avec les tubes verticaux
    });
    
    const tubeMesh = new THREE.Mesh(tubeGeometry, tubeMaterial);
    group.add(tubeMesh);
  }
  
  return group;
}

// Fonction utilitaire pour générer une géométrie de horn torus (tore à col resserré) avec col plus ouvert et proportions sphériques
function createHornTorusGeometry(R = 1, tube = 0.45, radialSegments = 42, tubularSegments = 42, phaseOffset = 0, harmonicData = null, selectedIndex = -1, isTherapyMode = false) {
  // Formule paramétrique du horn torus selon Däumler:
  // x = r·(1 − cosφ)·sinω
  // y = r·(1 − cosφ)·cosω
  // z = r·sinφ
  const geometry = new THREE.BufferGeometry();
  const positions = [];
  const indices = [];
  const normals = [];
  const uvs = [];
  const colors = []; // Ajouter les couleurs
  const r = R;
  
  for (let i = 0; i <= radialSegments; i++) {
    const φ = (i / radialSegments) * 2 * Math.PI - phaseOffset; // Lignes horizontales en sens inverse
    for (let j = 0; j <= tubularSegments; j++) {
      const ω = (j / tubularSegments) * 2 * Math.PI + phaseOffset; // Lignes verticales sens normal
      
      // Formule paramétrique exacte du horn torus
      let x = r * (1 - Math.cos(φ)) * Math.sin(ω);
      let y = r * (1 - Math.cos(φ)) * Math.cos(ω);
      const z = r * Math.sin(φ);
      
      positions.push(x, y, z);
      
      // Normales calculées à partir des dérivées partielles
      const nx = Math.sin(φ) * Math.sin(ω);
      const ny = Math.sin(φ) * Math.cos(ω);
      const nz = Math.cos(φ);
      normals.push(nx, ny, nz);
      
      uvs.push(i / radialSegments, j / tubularSegments);
      
      // 🌈 SYSTÈME PROGRESSIF HARMONISÉ THOR 42
      let color = new THREE.Color(1, 1, 1); // BLANC par défaut
      let tubeMultiplier = 1.0; // Facteur de grossissement du tube
      
      if (isTherapyMode && harmonicData && j < 42) {
        // 🎵 MODE THÉRAPIE SONORE - Couleurs inversées pour les 42 fréquences thérapeutiques
        // Toutes les 42 fréquences thérapeutiques sont disponibles depuis App.jsx
        
        // Couleur inversée par rapport au mode diagnostic
        const therapeuticColorIndex = 41 - j; // Inversion : 0→41, 1→40, etc.
        color = new THREE.Color(DIAGNOSTIC_COLORS[therapeuticColorIndex]);
        
        // Si cette ligne correspond à l'index sélectionné en thérapie
        if (j === selectedIndex) {
          color.multiplyScalar(1.5); // Plus lumineux si sélectionné
        }
      } else if (!isTherapyMode && harmonicData) {
        // Mode diagnostic: color verticals by frequency (octave folding)
        // Check if harmonicData contains metrics_42 (local analysis format)
        let segmentMetric = null;

        if (Array.isArray(harmonicData)) {
          // Array format: check if entry has metrics_42 or is itself a metric
          const entry = harmonicData[j];
          if (entry && entry.frequency !== undefined) {
            segmentMetric = entry;
          } else if (entry && entry.metrics_42 && entry.metrics_42[j]) {
            segmentMetric = entry.metrics_42[j];
          }
        } else if (harmonicData && typeof harmonicData === 'object') {
          // Object format: check for metrics_42 array directly
          if (harmonicData.metrics_42 && harmonicData.metrics_42[j]) {
            segmentMetric = harmonicData.metrics_42[j];
          } else if (harmonicData[j]) {
            const entry = harmonicData[j];
            if (entry.frequency !== undefined) {
              segmentMetric = entry;
            } else if (entry.metrics_42 && entry.metrics_42[j]) {
              segmentMetric = entry.metrics_42[j];
            }
          }
        }

        if (segmentMetric && segmentMetric.frequency > 0) {
          // Octave-folding color from real frequency data
          color = frequencyToColor(segmentMetric.frequency, (segmentMetric.resonance || 0) / 100);
          if (j === selectedIndex) {
            color.multiplyScalar(1.5);
          }
        } else if (segmentMetric && segmentMetric.isActive === false) {
          // Silent segment
          color = new THREE.Color(0.3, 0.3, 0.3);
        } else {
          // Fallback: check old completed indices logic
          let hasData = false;
          if (Array.isArray(harmonicData)) {
            hasData = harmonicData[j] && harmonicData[j].score > 0 && harmonicData[j].progress === 1;
          } else if (harmonicData[j]) {
            const d = harmonicData[j];
            hasData = d && d.score > 0 && d.progress === 1;
          }

          if (hasData) {
            color = new THREE.Color(DIAGNOSTIC_COLORS[j]);
            if (j === selectedIndex) color.multiplyScalar(1.5);
          } else {
            color = new THREE.Color(1.0, 1.0, 1.0);
          }
        }
      }
      
      colors.push(color.r, color.g, color.b);
      
      // Grossissement du tube SUPPRIMÉ pour éviter déformation du tore
      // if (tubeMultiplier > 1.0) { ... } // Code supprimé
    }
  }
  
  // Créer uniquement les lignes horizontales et verticales (pas de triangles)
  // Lignes radiales (verticales)
  for (let i = 0; i <= radialSegments; i++) {
    for (let j = 0; j < tubularSegments; j++) {
      const a = i * (tubularSegments + 1) + j;
      const b = i * (tubularSegments + 1) + (j + 1);
      indices.push(a, b);
    }
  }
  
  // Lignes tubulaires (horizontales)
  for (let i = 0; i < radialSegments; i++) {
    for (let j = 0; j <= tubularSegments; j++) {
      const a = i * (tubularSegments + 1) + j;
      const b = (i + 1) * (tubularSegments + 1) + j;
      indices.push(a, b);
    }
  }
  
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  return geometry;
}

const TorusAnimation = ({ 
  frequency, 
  liveFrequency, 
  isPlaying, 
  isRecording, 
  isReproducing, 
  alignmentState, 
  isHealing, 
  isMusyckMode,
  isGuidedRecording,
  diagnosticData,
  onDiagnosticLineClick,
  showDiagnosticPopup,
  selectedDiagnosticIndex,
  onDashboardCaseClick,
  // 🎵 PROPS THÉRAPIE SONORE - Copie parfaite du mode profil  
  therapyData,
  onTherapyFrequencyClick,
  showTherapyPopup,
  selectedTherapyIndex,
  onTherapyDashboardClick,
  onCloseDashboardIntro,
  getGlobalDashboardData,
  onHistorySelect,
  children,
  disablePopup = false,
  // 🎙️ PROPS MODE SELFLES
  isSelflesMode = false,
  selflesMetrics = null,
  // iOS port: live voice metrics from AudioEngine
  liveVolume = 0,
  liveResonance = 0,
  // Taille du torus au repos (landing page vs app)
  restRadius = 1,
  cameraFov = 50,
  cameraY = -3
}) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const torusRef = useRef(null);
  const animationRef = useRef(null);
  const timeRef = useRef(0);
  const lastFrequencyRef = useRef(0);
  const smoothRadius = useRef(restRadius);
  const smoothTube = useRef(0.2);
  const smoothOpacity = useRef(0.5);
  const smoothColor = useRef(new THREE.Color(1, 1, 1));
  const smoothRotationX = useRef(0);
  const smoothRotationY = useRef(0);
  const targetRotationX = useRef(0);
  const targetRotationY = useRef(0);
  const spiralPosition = useRef({ x: 0, y: 0, z: 0 });
  const breathingPhase = useRef(0);
  const restPhaseOffset = useRef(0);
  const verticalLinesRef = useRef([]);
  const raycasterRef = useRef(null);
  const mouseRef = useRef(new THREE.Vector2());
  const cameraRef = useRef(null);
  
  // Système d'auto-centrage et rotation manuelle
  const userRotationOffset = useRef(0); // Rotation ajoutée par l'utilisateur
  const targetAutoRotation = useRef(0); // Rotation cible pour auto-centrage
  const currentAutoRotation = useRef(0); // Rotation actuelle (lissée)
  const isDragging = useRef(false);
  const lastMouseX = useRef(0);
  const hasLoggedDiagnostic = useRef(false);

  // Resonance → tilt oscillation (ported from iOS TorusRenderer)
  const smoothedResonanceRef = useRef(0);
  const tiltPhaseRef = useRef(0);

  // Refs for props that change frequently — avoids stale closure AND prevents useEffect re-runs
  const liveVolumeRef = useRef(0);
  const liveResonanceRef = useRef(0);
  const liveFrequencyRef = useRef(null);
  const frequencyRef = useRef(null);
  const selflesMetricsRef = useRef(null);
  const isPlayingRef = useRef(false);
  const isRecordingRef = useRef(false);
  const isReproducingRef = useRef(false);
  const alignmentStateRef = useRef(null);
  const isHealingRef = useRef(false);
  const isMusyckModeRef = useRef(false);
  const isSelflesModeRef = useRef(false);
  const isGuidedRecordingRef = useRef(false);
  liveVolumeRef.current = liveVolume;
  liveResonanceRef.current = liveResonance;
  liveFrequencyRef.current = liveFrequency;
  frequencyRef.current = frequency;
  selflesMetricsRef.current = selflesMetrics;
  isPlayingRef.current = isPlaying;
  isRecordingRef.current = isRecording;
  isReproducingRef.current = isReproducing;
  alignmentStateRef.current = alignmentState;
  isHealingRef.current = isHealing;
  isMusyckModeRef.current = isMusyckMode;
  isSelflesModeRef.current = isSelflesMode;
  isGuidedRecordingRef.current = isGuidedRecording;

  // iOS port: silence → rest transition (0.3s)
  const wasActiveRef = useRef(false);
  const inactiveElapsedRef = useRef(0);
  const frozenScaleRef = useRef(1);
  const frozenResonanceRef = useRef(0);
  const lastColorRef = useRef(new THREE.Color(1, 1, 1));

  // États pour popup globale
  const [showGlobalPopup, setShowGlobalPopup] = useState(false);
  const [globalDiagnosticData, setGlobalDiagnosticData] = useState(null);

  // Recalculer globalDiagnosticData quand diagnosticData change (ex: sélection historique)
  useEffect(() => {
    if (showGlobalPopup && diagnosticData) {
      const globalData = getGlobalDashboardData ? getGlobalDashboardData() : extractGlobalData();
      setGlobalDiagnosticData(globalData);
    }
  }, [diagnosticData]);

  // Fonction pour charger un diagnostic historique
  const handleHistorySelect = (historicalDiagnosticData, metadata) => {
    console.log('🔄 TorusAnimation: Chargement diagnostic historique', metadata.timestamp);
    
    // Fermer le popup global
    setShowGlobalPopup(false);
    
    // Transmettre au parent (App.jsx) pour mise à jour complète
    if (onHistorySelect) {
      onHistorySelect(historicalDiagnosticData, metadata);
    }
  };

  // Fonction pour extraire les données globales du diagnostic complet
  const extractGlobalData = () => {
    if (diagnosticData && typeof diagnosticData === 'object') {
      const diagnosticEntries = Object.values(diagnosticData).filter(Boolean);

      if (diagnosticEntries.length > 0) {
        const sampleData = diagnosticEntries[0];

        // Local analysis format (has metrics_42 + features)
        if (sampleData.metrics_42 || sampleData.features?.f0_mean) {
          const features = sampleData.features || {};
          return {
            globalScore: sampleData.globalScore || 0,
            status: sampleData.globalScore >= 80 ? 'Excellent' : sampleData.globalScore >= 60 ? 'Bon' : 'Normal',
            metrics_42: sampleData.metrics_42,
            features: features
          };
        }

        // Backend format
        if (sampleData.analysis || sampleData.rawData) {
          const analysis = sampleData.analysis || sampleData;
          const rawData = sampleData.rawData || {};

          return {
            globalScore: analysis.globalScore || rawData.globalScore || 0,
            status: analysis.status || 'Analyse',
            mlPredictions: {
              prediction: analysis.mlPredictions?.prediction || analysis.prediction || 'en cours',
              confidence: analysis.mlPredictions?.confidence || analysis.confidence || 0
            },
            metrics: {
              fundamentalFrequency: analysis.metrics?.fundamentalFrequency || analysis.frequency || 0,
              jitter: analysis.metrics?.jitter || analysis.jitter || 0,
              shimmer: analysis.metrics?.shimmer || analysis.shimmer || 0,
              consistency: analysis.metrics?.consistency || analysis.consistency || 0
            }
          };
        }
      }
    }

    // No data yet
    return { globalScore: 0, status: 'En attente' };
  };


  // 🎯 AUTO-CENTRAGE LORS DU CHANGEMENT DE SÉLECTION VIA NAVIGATION CLAVIER
  useEffect(() => {
    if (selectedDiagnosticIndex !== -1 && showDiagnosticPopup && diagnosticData && diagnosticData[selectedDiagnosticIndex]) {
      const lineAngle = (selectedDiagnosticIndex / 42) * 2 * Math.PI;
      const targetCenterAngle = -lineAngle + Math.PI; // +180° pour mettre en face
      targetAutoRotation.current = targetCenterAngle;
      console.log(`⌨️ Navigation clavier: Auto-centrage ligne ${selectedDiagnosticIndex}: angle=${lineAngle.toFixed(2)} → rotation=${targetCenterAngle.toFixed(2)}`);
    }
  }, [selectedDiagnosticIndex, showDiagnosticPopup, diagnosticData]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Log unique à l'initialisation - vraiment unique cette fois !
    if (showDiagnosticPopup && diagnosticData && !hasLoggedDiagnostic.current) {
      // Compter seulement les diagnostics disponibles (0-10)
      const validDiagnostics = Object.keys(diagnosticData)
        .map(k => parseInt(k))
        .filter(k => !isNaN(k) && k < 42);
      console.log('🌈 Mode diagnostic activé');
      console.log('📊 Diagnostics valides:', validDiagnostics.length, 'sur 42');
      hasLoggedDiagnostic.current = true;
    } else if (!showDiagnosticPopup) {
      hasLoggedDiagnostic.current = false; // Reset quand on quitte le mode diagnostic
    }

    const scene = new THREE.Scene();
    const container = containerRef.current;
    const width = container?.clientWidth || window.innerWidth;
    const height = container?.clientHeight || window.innerHeight;
    const initAspect = width / height;
    const initFov = initAspect < 1.5
      ? 2 * Math.atan(Math.tan(cameraFov / 2 * Math.PI / 180) * (1.5 / initAspect)) * 180 / Math.PI
      : cameraFov;
    const camera = new THREE.PerspectiveCamera(initFov, initAspect, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);
    
    // Initialiser le raycaster pour la détection de clics
    raycasterRef.current = new THREE.Raycaster();
    raycasterRef.current.params.Line.threshold = 0.5; // Zone de clic BEAUCOUP plus large

    // Configuration harmonique progressive THOR 42 🌈📈
    // En mode diagnostic : utiliser les vraies données (progressives)
    // En mode thérapie : utiliser les données thérapeutiques (couleurs inversées)
    // En mode normal : pas de couleurs (blanc classique)
    const harmonicData = (showDiagnosticPopup && diagnosticData) ? diagnosticData : 
                        (showTherapyPopup && therapyData) ? therapyData : null;
    const selectedIndex = showDiagnosticPopup ? selectedDiagnosticIndex : 
                         showTherapyPopup ? selectedTherapyIndex : -1;
    
    const geometry = createHornTorusGeometry(restRadius, 1.8, 42, 42, 0, harmonicData, selectedIndex || -1, showTherapyPopup);
    const useVertexColors = !!(showDiagnosticPopup || showTherapyPopup);
    const material = new THREE.LineBasicMaterial({
      color: useVertexColors ? 0xffffff : (isHealing ? 0x88ff88 : 0xffffff),
      transparent: true,
      opacity: 0.5,
      linewidth: useVertexColors ? 5 : 2,
      vertexColors: useVertexColors
    });
    
    const torus = new THREE.LineSegments(geometry, material);
    
    // 🗑️ Éclairage supprimé - plus besoin avec les tubes
    
    // 🎯 GROUPE POUR TORE + CYLINDRES GROSSIS + ZONES DE CLIC
    const torusGroup = new THREE.Group();
    torusGroup.add(torus);
    scene.add(torusGroup);
    torusRef.current = torusGroup; // Reference le groupe, pas juste les lignes
    sceneRef.current = scene;
    
    // ✨ SIMPLICITÉ : Juste le tore avec couleurs intégrées + clics directs
    
    // Les 42 lignes verticales du tore correspondent directement aux 42 harmoniques
    // (Logs déplacés vers l'initialisation pour éviter le spam)

    // Position caméra
    camera.position.x = 0;
    camera.position.y = cameraY;
    camera.position.z = -3;
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;


    // Animation
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      
      if (torusRef.current) {
        timeRef.current += 0.016;
        let targetRadius = 1;
        let targetTube = 0.2;
        let targetColor = new THREE.Color(1, 1, 1);
        let targetOpacity = 0.5;

        // Réinitialiser la position du Torus
        torusRef.current.position.set(0, 0, 0);

        if (isRecordingRef.current && liveFrequencyRef.current) {
          // iOS port: frequency→color, volume→scale, resonance→tilt
          const freq = liveFrequencyRef.current;
          targetColor = frequencyToColor(freq);

          // Volume → Scale: restRadius + volume proportional growth
          const vol = liveVolumeRef.current;
          targetRadius = restRadius + vol * restRadius * 0.5;

          targetTube = 0.2;
          targetOpacity = 0.7;

          // Resonance → Tilt oscillation (match iOS: ±30° sway at 0.4Hz)
          const res = liveResonanceRef.current;
          smoothedResonanceRef.current += (res - smoothedResonanceRef.current) * 0.1;
          tiltPhaseRef.current += 0.016 * 0.4 * 2 * Math.PI;
          targetRotationX.current = smoothedResonanceRef.current * (Math.PI / 6) * Math.sin(tiltPhaseRef.current);
          targetRotationY.current = 0;

          // Track active state for rest transition
          wasActiveRef.current = true;
          inactiveElapsedRef.current = 0;
          frozenScaleRef.current = smoothRadius.current;
          frozenResonanceRef.current = smoothedResonanceRef.current;
          lastColorRef.current.copy(smoothColor.current);

          // Active: freeze rest phase (no rest rotation during voice)

        } else if ((isPlayingRef.current && frequencyRef.current) || (isHealingRef.current && frequencyRef.current) || (isMusyckModeRef.current && frequencyRef.current)) {
          // Playback/healing: sinusoidal animation
          const freq = frequencyRef.current;
          targetColor = frequencyToColor(freq);

          const baseSpeed = freq / 400;
          const rhythmSpeed = baseSpeed * 0.5;
          const pulseAmount = 0.25 * (freq / 2000);

          const slowPulse = Math.sin(timeRef.current * baseSpeed) * pulseAmount;
          const rhythmPulse = Math.sin(timeRef.current * rhythmSpeed * 2) * (pulseAmount * 0.3);

          targetRadius = 1 + slowPulse + rhythmPulse;
          targetTube = 0.2 + Math.cos(timeRef.current * baseSpeed) * (pulseAmount * 0.2);
          targetOpacity = 0.6 + Math.sin(timeRef.current * baseSpeed) * 0.1;

          const φ = 1.618033988749895;
          const rotationSpeed = baseSpeed * 0.3;
          const rotationAmplitude = 0.5 / φ;

          targetRotationX.current = Math.sin(timeRef.current * rotationSpeed) * rotationAmplitude;
          targetRotationY.current = Math.cos(timeRef.current * rotationSpeed) * rotationAmplitude;

        } else if (isSelflesModeRef.current && liveFrequencyRef.current) {
          // MODE SELFLES — ported from iOS: frequency→color, volume→scale, resonance→tilt
          targetColor = frequencyToColor(liveFrequencyRef.current);

          // Volume → Scale: restRadius + volume proportional growth
          const vol = selflesMetricsRef.current ? selflesMetricsRef.current.volume : 0;
          targetRadius = restRadius + vol * restRadius * 0.5;

          targetTube = 0.2;
          targetOpacity = 0.7;

          // Resonance → Tilt oscillation (match iOS: ±30° sway at 0.4Hz)
          const resonance = selflesMetricsRef.current ? (selflesMetricsRef.current.resonance || 0) : 0;
          smoothedResonanceRef.current += (resonance - smoothedResonanceRef.current) * 0.1;
          tiltPhaseRef.current += 0.016 * 0.4 * 2 * Math.PI; // ~60fps × 0.4Hz
          targetRotationX.current = smoothedResonanceRef.current * (Math.PI / 6) * Math.sin(tiltPhaseRef.current);
          targetRotationY.current = 0;

        } else if (isReproducingRef.current && liveFrequencyRef.current && alignmentStateRef.current) {
          // Mode reproduction : animation plus réactive
          if (alignmentStateRef.current === 'perfect') {
            const pulseSpeed = 2.5; // Augmenté pour plus de réactivité
            const pulseIntensity = 0.3 + 0.2 * Math.sin(timeRef.current * pulseSpeed);
            targetRadius = 1 + pulseIntensity;
            targetTube = 0.2 * (1 - pulseIntensity * 0.3);
            targetColor = new THREE.Color(1, 1, 1);
            targetOpacity = 0.7 + 0.3 * Math.sin(timeRef.current * pulseSpeed);
          } else if (alignmentStateRef.current === 'close') {
            const pulseSpeed = 1.5;
            const pulseIntensity = 0.2 * Math.sin(timeRef.current * pulseSpeed);
            targetRadius = 1 + pulseIntensity;
            targetTube = 0.2 * (1 - pulseIntensity * 0.2);
            targetColor = new THREE.Color();
            targetColor.setHSL(0.08, 0.8, 0.6);
            targetOpacity = 0.6 + 0.2 * Math.sin(timeRef.current * pulseSpeed);
          } else {
            targetRadius = 1;
            targetTube = 0.2;
            targetColor = new THREE.Color();
            targetColor.setHSL(0, 0.7, 0.3);
            targetOpacity = 0.5;
          }
        } else {
          if (wasActiveRef.current) {
            // Silence → Rest transition (0.3s, match iOS)
            inactiveElapsedRef.current += 0.016;
            const restProgress = Math.min(inactiveElapsedRef.current / 0.3, 1);

            targetRadius = frozenScaleRef.current + (restRadius - frozenScaleRef.current) * restProgress;
            targetTube = 0.2;
            targetColor = lastColorRef.current.clone().lerp(new THREE.Color(1, 1, 1), restProgress);
            targetOpacity = 0.5 + 0.2 * (1 - restProgress);

            // Resonance decays to 0
            const transResonance = frozenResonanceRef.current * (1 - restProgress);
            tiltPhaseRef.current += 0.016 * 0.4 * 2 * Math.PI;
            targetRotationX.current = transResonance * (Math.PI / 6) * Math.sin(tiltPhaseRef.current);
            targetRotationY.current = 0;

            // Rest rotation resumes progressively (accumulative, match iOS)
            restPhaseOffset.current += 0.016 * 0.1 * restProgress;

            if (restProgress >= 1) {
              wasActiveRef.current = false;
            }
          } else {
            // Normal rest state
            targetRadius = restRadius;
            targetTube = 0.2;
            targetColor.set(0xffffff);
            targetOpacity = 0.5;
            targetRotationX.current = 0;
            targetRotationY.current = 0;

            // Rest rotation (accumulative, match iOS) — stop in diagnostic mode
            if (!showDiagnosticPopup) {
              restPhaseOffset.current += 0.016 * 0.1;
            }
          }
        }

        // Lissage adapté à chaque mode — match iOS TorusRenderer
        const lerp = (a, b, t) => a + (b - a) * t;
        const colorSmoothFactor = isGuidedRecordingRef.current ? 0.015 : 0.15;  // iOS colorSmoothFactor
        const scaleSmoothFactor = isGuidedRecordingRef.current ? 0.015 : 0.1;   // iOS scaleSmoothFactor

        smoothRadius.current = lerp(smoothRadius.current, targetRadius, scaleSmoothFactor);
        smoothTube.current = lerp(smoothTube.current, targetTube, scaleSmoothFactor);
        smoothOpacity.current = lerp(smoothOpacity.current, targetOpacity, scaleSmoothFactor);
        smoothColor.current.lerp(targetColor, colorSmoothFactor);

        // Lissage des rotations — match iOS tilt lerp 0.1
        const rotationSmoothFactor = isGuidedRecording ? 0.01 : 0.1;
        smoothRotationX.current = lerp(smoothRotationX.current, targetRotationX.current, rotationSmoothFactor);
        smoothRotationY.current = lerp(smoothRotationY.current, targetRotationY.current, rotationSmoothFactor);

        // Application des rotations lissées
        torusRef.current.rotation.x = smoothRotationX.current;
        torusRef.current.rotation.y = smoothRotationY.current;

        // Auto-centrage : lisser vers la rotation cible (utilise la fonction lerp déjà définie)
        currentAutoRotation.current = lerp(currentAutoRotation.current, targetAutoRotation.current, 0.05);
        
        // Mise à jour hybride selon le mode
        const currentTorus = torusRef.current.children[0];
        const phaseOffset = restPhaseOffset.current + currentAutoRotation.current + userRotationOffset.current;
        
        // Mise à jour harmonique avec couleurs toujours actives ! 🌈
        if (currentTorus && currentTorus.geometry) {
          currentTorus.geometry.dispose();
          
          // Utiliser les données diagnostic progressives en mode diagnostic/thérapie
          const harmonicData = (showDiagnosticPopup && diagnosticData) ? diagnosticData : 
                              (showTherapyPopup && therapyData) ? therapyData : null;
          const selectedIndex = showDiagnosticPopup ? selectedDiagnosticIndex : 
                               showTherapyPopup ? selectedTherapyIndex : -1;
          
          currentTorus.geometry = createHornTorusGeometry(
            smoothRadius.current,
            1.8 * (smoothTube.current / 0.2),
            42,
            42,
            phaseOffset,
            harmonicData, // Couleurs progressives en mode diagnostic/thérapie ! 🌈📈
            selectedIndex || -1, // Ligne sélectionnée
            showTherapyPopup // Mode thérapie
          );
          
          // Mise à jour du matériau
          if (currentTorus.material) {
            const useVC = !!(showDiagnosticPopup || showTherapyPopup);
            if (useVC) {
              // Mode profil/thérapie : vertex colors par fréquence, pas de couleur uniforme
              currentTorus.material.vertexColors = true;
              currentTorus.material.color.set(0xffffff);
            } else {
              // Mode live : couleur uniforme lissée
              currentTorus.material.vertexColors = false;
              currentTorus.material.color.copy(smoothColor.current);
            }
            currentTorus.material.opacity = smoothOpacity.current;
            currentTorus.material.linewidth = useVC ? 5 : 2;
            currentTorus.material.needsUpdate = true;
          }
        }
        
        // Les couleurs sont maintenant intégrées dans la géométrie
      }
      
      renderer.render(scene, camera);
    };

    animate();

    // Gestion du redimensionnement avec mise à jour de la caméra
    const baseFov = cameraFov;
    const baseAspect = 1.5; // reference aspect where torus looks correct
    const handleResize = () => {
      const c = containerRef.current;
      const w = c?.clientWidth || window.innerWidth;
      const h = c?.clientHeight || window.innerHeight;
      const aspect = w / h;
      camera.aspect = aspect;
      // Adjust FOV so torus keeps same apparent size in any aspect ratio
      if (aspect < baseAspect) {
        camera.fov = 2 * Math.atan(Math.tan(baseFov / 2 * Math.PI / 180) * (baseAspect / aspect)) * 180 / Math.PI;
      } else {
        camera.fov = baseFov;
      }
      console.log('[TORUS RESIZE]', { w, h, aspect: aspect.toFixed(2), fov: camera.fov.toFixed(1), smoothRadius: smoothRadius.current.toFixed(3) });
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    // Also listen to orientationchange (mobile)
    window.addEventListener('orientationchange', () => {
      console.log('[TORUS ORIENTATION]', window.screen.orientation?.type);
      setTimeout(handleResize, 100);
    });

    // Gestion des clics sur les lignes verticales du tore
    const handleClick = (event) => {
      // Si disablePopup est activé, ne pas traiter le clic
      if (disablePopup) {
        console.log('🚫 Click disabled on homepage');
        return;
      }
      
      console.log('🖱️ Click detected on tore, diagnostic mode:', showDiagnosticPopup);
      
      // Calculer la position de la souris normalisée
      const rect = renderer.domElement.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      console.log('📍 Mouse position:', mouseRef.current.x, mouseRef.current.y);
      
      // 🎯 DÉTECTION HYBRIDE : tubes en diagnostic, LineSegments sinon
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      const intersects = raycasterRef.current.intersectObject(torusRef.current, true);
      
      console.log('🎯 Intersects found:', intersects.length);
      
      if (intersects.length > 0) {
        let diagnosticIndex = -1;
        
        // Calcul par position pour tous les modes
        if (diagnosticIndex === -1) {
          const intersect = intersects[0];
          const point = intersect.point;
          const angle = Math.atan2(point.y, point.x);
          let normalizedAngle = (angle + Math.PI) / (2 * Math.PI);
          diagnosticIndex = Math.floor(normalizedAngle * 42);
          console.log('🔢 Ligne calculée par position:', diagnosticIndex, 'angle:', angle.toFixed(2));
        }
        
        // 🎯 AUTO-CENTRAGE MAGIQUE : amener la ligne AU CENTRE DEVANT NOUS
        const lineAngle = (diagnosticIndex / 42) * 2 * Math.PI; // Position de cette ligne
        const targetCenterAngle = -lineAngle; // Rotation pour l'amener face à nous (centre)
        targetAutoRotation.current = targetCenterAngle;
        
        console.log(`🎯 Auto-centrage ligne ${diagnosticIndex}: angle=${lineAngle.toFixed(2)} → rotation=${targetCenterAngle.toFixed(2)}`);
        
        // 🎯 POPUP GAUCHE : Dashboard global ou thérapeutique selon le mode
        if (showTherapyPopup && onTherapyDashboardClick) {
          // 🎵 MODE THÉRAPIE : Ouvrir le dashboard thérapeutique
          console.log('🎵 Opening therapy dashboard via click on torus');
          onTherapyDashboardClick();
        } else {
          // 🎯 MODE DIAGNOSTIC : Afficher le profil global
          // Fermer d'abord le dashboard intro pour éviter les doublons
          if (onCloseDashboardIntro) {
            onCloseDashboardIntro();
          }
          // Utiliser la même fonction que App.jsx pour cohérence des données
          const globalData = getGlobalDashboardData ? getGlobalDashboardData() : extractGlobalData();
          setGlobalDiagnosticData(globalData);
          setShowGlobalPopup(true);
        }
        
        // 🎯 POPUP DROITE : Harmonique spécifique ou fréquence thérapeutique
        if (showDiagnosticPopup && diagnosticData) {
          // 🔧 CORRECTION : Convertir ligne visuelle → harmonique réelle
          // Recalculer completedIndices comme dans la génération des couleurs
          let completedIndices = [];
          const AVAILABLE_DIAGNOSTICS = 42; // 🎉 DÉBLOCAGE COMPLET : toutes les 42 harmoniques THOR disponibles !
          
          if (Array.isArray(diagnosticData)) {
            completedIndices = diagnosticData
              .map((data, idx) => {
                return (data && data.score && data.score > 0 && data.progress === 1) ? idx : null;
              })
              .filter(idx => idx !== null && idx < AVAILABLE_DIAGNOSTICS)
              .sort((a, b) => a - b);
          }
          
          // diagnosticIndex = ligne visuelle (0, 1, 2...)
          // completedIndices[diagnosticIndex] = vraie harmonique (0, 2, 5...)
          const realEquationIndex = completedIndices[diagnosticIndex];
          
          if (realEquationIndex !== undefined && diagnosticData[realEquationIndex]) {
            if (onDiagnosticLineClick) {
              console.log('📞 Calling onDiagnosticLineClick - Ligne visuelle:', diagnosticIndex, '→ Harmonique réelle:', realEquationIndex);
              onDiagnosticLineClick(realEquationIndex, null, null);
              console.log('🔄 Double popup activée - Gauche: profil global, Droite: harmonique', realEquationIndex);
            } else {
              console.log('❌ onDiagnosticLineClick not available');
            }
          } else {
            console.log('🔄 Ligne cliquée sans données diagnostic:', diagnosticIndex);
          }
        } else if (showTherapyPopup && therapyData && therapyData[diagnosticIndex]) {
          // 🎵 MODE THÉRAPIE : Fréquence thérapeutique cliquée
          if (onTherapyFrequencyClick) {
            console.log('🎵 Calling onTherapyFrequencyClick with:', diagnosticIndex);
            onTherapyFrequencyClick(diagnosticIndex, null, null);
            console.log('🔄 Thérapie popup activée - Fréquence', diagnosticIndex);
          } else {
            console.log('❌ onTherapyFrequencyClick not available');
          }
        } else {
          console.log('🔄 Popup gauche seule activée - Mode fallback ou hors diagnostic/thérapie (ligne', diagnosticIndex, ')');
        }
      } else {
        console.log('❌ No intersection with tore detected');
      }
    };
    
    renderer.domElement.addEventListener('click', handleClick);
    
    // 👆 ROTATION MANUELLE avec souris/tactile
    const handleMouseDown = (event) => {
      if (showDiagnosticPopup) {
        isDragging.current = true;
        lastMouseX.current = event.clientX;
        renderer.domElement.style.cursor = 'grabbing';
        console.log('🖱️ Début rotation manuelle');
      }
    };
    
    const handleMouseMove = (event) => {
      if (isDragging.current && showDiagnosticPopup) {
        const deltaX = event.clientX - lastMouseX.current;
        const rotationSpeed = 0.01; // Sensibilité de rotation
        userRotationOffset.current += deltaX * rotationSpeed;
        lastMouseX.current = event.clientX;
        return; // Ne pas faire le hover si on est en train de faire glisser
      }
      
      // Gestion du curseur sur hover (seulement si pas en train de glisser)
      if (!isDragging.current) {
        handleHover(event);
      }
    };
    
    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        renderer.domElement.style.cursor = 'default';
        console.log('🖱️ Fin rotation manuelle');
      }
    };
    
    // Événements tactiles pour mobile
    const handleTouchStart = (event) => {
      if (showDiagnosticPopup && event.touches.length === 1) {
        isDragging.current = true;
        lastMouseX.current = event.touches[0].clientX;
        console.log('👆 Début rotation tactile');
      }
    };
    
    const handleTouchMove = (event) => {
      if (isDragging.current && showDiagnosticPopup && event.touches.length === 1) {
        event.preventDefault(); // Empêcher le scroll de la page
        const deltaX = event.touches[0].clientX - lastMouseX.current;
        const rotationSpeed = 0.01;
        userRotationOffset.current += deltaX * rotationSpeed;
        lastMouseX.current = event.touches[0].clientX;
      }
    };
    
    const handleTouchEnd = () => {
      if (isDragging.current) {
        isDragging.current = false;
        console.log('👆 Fin rotation tactile');
      }
    };
    
    // Attacher tous les événements
    renderer.domElement.addEventListener('mousedown', handleMouseDown);
    renderer.domElement.addEventListener('mousemove', handleMouseMove);
    renderer.domElement.addEventListener('mouseup', handleMouseUp);
    renderer.domElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    renderer.domElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    renderer.domElement.addEventListener('touchend', handleTouchEnd);
    
    // Fonction séparée pour la gestion du hover
    const handleHover = (event) => {
      if (!showDiagnosticPopup) return;
      
      const rect = renderer.domElement.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      const intersects = raycasterRef.current.intersectObject(torusRef.current, true);
      
      if (intersects.length > 0) {
        // 🎯 DÉTECTION HOVER HYBRIDE
        let diagnosticIndex = -1;
        
        // Calcul par position direct
        if (diagnosticIndex === -1) {
          const intersect = intersects[0];
          const point = intersect.point;
          const angle = Math.atan2(point.y, point.x);
          let normalizedAngle = (angle + Math.PI) / (2 * Math.PI);
          diagnosticIndex = Math.floor(normalizedAngle * 42);
        }
        
        // Feedback visuel amélioré pour les lignes cliquables
        const hasEquation = diagnosticData && diagnosticData[diagnosticIndex];
        if (hasEquation) {
          renderer.domElement.style.cursor = 'pointer';
          renderer.domElement.title = `Métrique ${diagnosticIndex + 1}: Cliquez pour voir les détails`;
        } else {
          renderer.domElement.style.cursor = isDragging.current ? 'grabbing' : 'default';
          renderer.domElement.title = '';
        }
      } else {
        renderer.domElement.style.cursor = isDragging.current ? 'grabbing' : 'default';
      }
    };
    
    renderer.domElement.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    return () => {
      renderer.domElement.removeEventListener('click', handleClick);
      renderer.domElement.removeEventListener('mousedown', handleMouseDown);
      renderer.domElement.removeEventListener('mousemove', handleMouseMove);
      renderer.domElement.removeEventListener('mouseup', handleMouseUp);
      renderer.domElement.removeEventListener('touchstart', handleTouchStart);
      renderer.domElement.removeEventListener('touchmove', handleTouchMove);
      renderer.domElement.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
      if (torusRef.current && torusRef.current.geometry) {
        torusRef.current.geometry.dispose();
      }
      if (torusRef.current && torusRef.current.material) {
        torusRef.current.material.dispose();
      }
      // Les lignes verticales font partie de la géométrie principale, pas besoin de nettoyage séparé
      renderer.dispose();
    };
  }, [diagnosticData, showDiagnosticPopup, selectedDiagnosticIndex, onDiagnosticLineClick, therapyData, showTherapyPopup, selectedTherapyIndex, onTherapyFrequencyClick, restRadius, cameraFov, cameraY]);

  // Nom de classe basé sur l'état d'alignement pour les animations CSS
  let animationClass = '';
  if (isReproducing && alignmentState) {
    animationClass = alignmentState;
  } else if (isHealing) {
    animationClass = 'healing';
  }

  return (
    <div className="torus-wrapper">
      <div ref={containerRef} className={`torus-canvas ${animationClass}`} />
      
      {/* Indicateur supprimé — la popup DiagnosticPopup à droite affiche toutes les infos */}
      
      {/* 🩺 POPUP GLOBALE À GAUCHE */}
      {showGlobalPopup && globalDiagnosticData && (
        <GlobalDiagnosticPopup 
          data={globalDiagnosticData}
          diagnosticData={diagnosticData}
          onClose={() => setShowGlobalPopup(false)}
          onCaseClick={onDashboardCaseClick}
          onHistorySelect={handleHistorySelect}
        />
      )}
      
      <div className="button-container">
        {children}
      </div>
    </div>
  );
};

export default TorusAnimation;
